import { useState } from "react";
import { FiCheck } from "react-icons/fi";
import LayoutDisclosure from "../LayoutDisclosure";
import {
  getLayoutPresets,
  isLayoutPresetActive,
  isPresetTypographyActive,
} from "./layoutPresets";
import "./QuickLayoutPresets.css";

const PRESETS_PER_PAGE = 4;

export default function QuickLayoutPresets({ options, fontStyle, onApply }) {
  const blockType = options?.block_type?.toLowerCase();
  const presets = getLayoutPresets(options?.block_type);
  const [pageState, setPageState] = useState({ blockType, page: 0 });
  const pageCount = Math.ceil(presets.length / PRESETS_PER_PAGE);
  const page = pageState.blockType === blockType ? pageState.page : 0;
  const visiblePage = Math.min(page, Math.max(pageCount - 1, 0));
  const pageStart = visiblePage * PRESETS_PER_PAGE;
  const visiblePresets = presets.slice(
    pageStart,
    pageStart + PRESETS_PER_PAGE,
  );

  return (
    <LayoutDisclosure title="Quick Presets">
      <div
        className={`sx-quick-presets sx-quick-presets--page-${visiblePage}`}
        role="list"
        aria-label="Layout presets"
        key={`${blockType || "fallback"}-${visiblePage}`}
      >
        {visiblePresets.map((item) => {
          const isActive =
            isLayoutPresetActive(options, item.values) &&
            isPresetTypographyActive(fontStyle, item.typography);

          return (
            <div role="listitem" key={item.id}>
              <button
                type="button"
                className={`sx-quick-presets__button${
                  isActive ? " sx-quick-presets__button--active" : ""
                }`}
                aria-pressed={isActive}
                disabled={isActive}
                onClick={() =>
                  item.typography
                    ? onApply(item.values, item.typography)
                    : onApply(item.values)
                }
              >
                <span className="sx-quick-presets__label">
                  {item.label}
                  {isActive && <FiCheck aria-hidden="true" />}
                </span>
                <span className="sx-quick-presets__detail">{item.detail}</span>
              </button>
            </div>
          );
        })}
      </div>
      {pageCount > 1 && (
        <div
          className="sx-quick-presets__pagination"
          role="group"
          aria-label="Preset pages"
        >
          {Array.from({ length: pageCount }, (_, index) => {
            const firstPreset = index * PRESETS_PER_PAGE + 1;
            const lastPreset = Math.min(
              firstPreset + PRESETS_PER_PAGE - 1,
              presets.length,
            );
            const isCurrent = visiblePage === index;

            return (
              <button
                type="button"
                key={index}
                className={`sx-quick-presets__page-dot${
                  isCurrent ? " sx-quick-presets__page-dot--active" : ""
                }`}
                aria-label={`Show presets ${firstPreset}–${lastPreset}`}
                aria-current={isCurrent ? "page" : undefined}
                onClick={() => setPageState({ blockType, page: index })}
              />
            );
          })}
        </div>
      )}
    </LayoutDisclosure>
  );
}
